import React, { useState, useEffect, useRef } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from 'chess.js';
import { useTranslation } from 'react-i18next';
import { HEADER_HEIGHT } from '../config/Constants';
import PuzzleService from "../services/PuzzleService";
import GameUtils from '../utils/gameUtils';

import { View, Text, TouchableOpacity } from 'react-native';

const STATS_KEY = 'sumo_puzzle_stats';
const FAILED_KEY = 'sumo_puzzle_failed_ids';   // {puzzleId: true} — counted once each
const NUM_INSULTS = 20;

// Ten (insulting) ranks. Rank is driven by an accuracy-weighted score, not raw
// volume: effective = solved * (solved / (solved + failed)). So a sloppy player
// (many failed puzzles) needs far more solves to climb than a clean one.
const LEVEL_MIN = [0, 3, 7, 13, 21, 31, 46, 66, 91, 121];
const levelIndex = (score) => {
  let i = 0;
  for (let k = 0; k < LEVEL_MIN.length; k++) if (score >= LEVEL_MIN[k]) i = k;
  return i;
};
// Fraction of solved puzzles done without a single wrong move.
const accuracyPct = (solved, failed) => {
  const total = (solved || 0) + (failed || 0);
  return total > 0 ? Math.round((solved / total) * 100) : 100;
};
// Accuracy-weighted score feeding the rank ladder.
const effectiveScore = (solved, failed) => {
  if (!solved || solved <= 0) return 0;
  return solved * (solved / (solved + (failed || 0)));
};

// ISO day string (YYYY-MM-DD) for streak bookkeeping.
const todayISO = () => new Date().toISOString().slice(0, 10);

// Is `cur` exactly one calendar day after `prev`? (both YYYY-MM-DD)
const isNextDay = (prev, cur) => {
  if (!prev || !cur) return false;
  const a = new Date(prev + 'T00:00:00');
  const b = new Date(cur + 'T00:00:00');
  return Math.round((b - a) / 86400000) === 1;
};

const pad2 = (n) => (n < 10 ? '0' : '') + n;
// YYYY-MM-DD for a given year/month(0-based)/day.
const isoOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
// A local Date at midnight from a YYYY-MM-DD string (avoids TZ drift).
const dateFromISO = (s) => new Date(s + 'T00:00:00');

// Puzzle of the Day. Three puzzles per day (easy / medium / hard); the player is
// the side to move and must find the forced mate. Each attacker move is confirmed
// server-side (the solution never reaches the browser); the forced defender reply
// is played automatically. Sumo push/check rules come from GameUtils.
export default function Puzzle() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth, height: window.innerHeight,
  });
  const [puzzles, setPuzzles] = useState([]);       // up to 3, ordered by slot
  const [selected, setSelected] = useState(0);      // index into puzzles
  const [position, setPosition] = useState(null);   // FEN driving the board
  const [status, setStatus] = useState('loading');  // loading|none|playing|solved|error
  const [message, setMessage] = useState("");
  const [msgKind, setMsgKind] = useState("");       // ''|wrong|keep|solved
  const [alertMsg, setAlertMsg] = useState("");     // big flashed alert over the board
  const [lastArrow, setLastArrow] = useState([]);
  const [solved, setSolved] = useState({});         // {puzzleId: true}
  const [stats, setStats] = useState({ totalSolved: 0, failed: 0, streak: 0, bestStreak: 0, lastDate: null });

  // Calendar / date navigation.
  const [availDates, setAvailDates] = useState(() => new Set()); // YYYY-MM-DD with puzzles
  const [selectedDate, setSelectedDate] = useState(null);        // day being played (YYYY-MM-DD)
  const [showCal, setShowCal] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const gameRef = useRef(null);
  const lastMoveRef = useRef(null);
  const plyRef = useRef(0);
  const guRef = useRef(null);
  const busyRef = useRef(false);
  const puzzleRef = useRef(null);   // the currently-selected puzzle
  const alertTimer = useRef(null);  // auto-dismiss timer for the flashed alert
  const failedIdsRef = useRef({});  // puzzle ids already counted as failed

  const DIFF_LABELS = {
    easy: t('difficulty_easy') || 'Easy',
    medium: t('difficulty_medium') || 'Medium',
    hard: t('difficulty_hard') || 'Hard',
  };

  useEffect(() => {
    const onResize = () => setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Restore lifetime stats (solved count + day streak).
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STATS_KEY) || 'null');
      if (s && typeof s === 'object') {
        setStats({
          totalSolved: s.totalSolved || 0,
          failed: s.failed || 0,
          streak: s.streak || 0,
          bestStreak: s.bestStreak || 0,
          lastDate: s.lastDate || null,
        });
      }
    } catch (e) { /* ignore */ }
    try {
      failedIdsRef.current = JSON.parse(localStorage.getItem(FAILED_KEY) || '{}') || {};
    } catch (e) { failedIdsRef.current = {}; }
  }, []);

  const randomInsult = () =>
    t('insult_' + (1 + Math.floor(Math.random() * NUM_INSULTS)));

  // Flash a big, impossible-to-miss alert over the board; auto-dismisses.
  const flashAlert = (msg) => {
    setAlertMsg(msg);
    if (alertTimer.current) clearTimeout(alertTimer.current);
    alertTimer.current = setTimeout(() => setAlertMsg(""), 2800);
  };
  const clearAlert = () => {
    if (alertTimer.current) { clearTimeout(alertTimer.current); alertTimer.current = null; }
    setAlertMsg("");
  };
  useEffect(() => () => { if (alertTimer.current) clearTimeout(alertTimer.current); }, []);

  // Count a freshly-solved puzzle: +1 to the total, and advance the day streak
  // the first time a puzzle is solved on a new calendar day.
  const recordSolve = () => {
    setStats((prev) => {
      const day = todayISO();
      let streak;
      if (prev.lastDate === day) streak = prev.streak || 1;      // already counted today
      else if (isNextDay(prev.lastDate, day)) streak = (prev.streak || 0) + 1;
      else streak = 1;
      const next = {
        totalSolved: (prev.totalSolved || 0) + 1,
        failed: prev.failed || 0,
        streak,
        bestStreak: Math.max(prev.bestStreak || 0, streak),
        lastDate: day,
      };
      try { localStorage.setItem(STATS_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  // Count a puzzle as failed the first time a wrong solution is played on it.
  const recordFail = (pz) => {
    if (failedIdsRef.current[pz.id]) return;
    failedIdsRef.current[pz.id] = true;
    try { localStorage.setItem(FAILED_KEY, JSON.stringify(failedIdsRef.current)); } catch (e) { /* ignore */ }
    setStats((prev) => {
      const next = { ...prev, failed: (prev.failed || 0) + 1 };
      try { localStorage.setItem(STATS_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  // Load a day's set of puzzles. `dateStr` null = today.
  const loadPuzzles = (dateStr) => {
    setStatus('loading');
    setShowCal(false);
    PuzzleService.getDaily(dateStr)
      .then((resp) => {
        const data = resp.data || {};
        const list = data.puzzles || [];
        const day = data.date || dateStr || todayISO();
        setSelectedDate(day);
        if (!list.length) { setPuzzles([]); setStatus('none'); return; }
        // restore solved state for this day from localStorage
        let done = {};
        try {
          const key = 'sumo_puzzle_solved_' + (list[0].puzzle_date || day);
          done = JSON.parse(localStorage.getItem(key) || '{}');
        } catch (e) { done = {}; }
        setSolved(done);
        setPuzzles(list);
        // start on the first unsolved puzzle, else the first
        const firstUnsolved = list.findIndex((p) => !done[p.id]);
        selectPuzzle(list, firstUnsolved >= 0 ? firstUnsolved : 0, done);
      })
      .catch(() => setStatus('error'));
  };

  // On mount: fetch the calendar's playable days, then load today's set.
  useEffect(() => {
    PuzzleService.getDates()
      .then((resp) => {
        const ds = (resp.data && resp.data.dates) || [];
        setAvailDates(new Set(ds));
      })
      .catch(() => { /* calendar just stays empty */ });
    loadPuzzles(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up the board for puzzle at `idx`.
  const selectPuzzle = (list, idx, doneMap) => {
    const pz = list[idx];
    if (!pz) return;
    setSelected(idx);
    puzzleRef.current = pz;
    gameRef.current = new Chess(pz.fen);
    lastMoveRef.current = pz.last_move || null;
    plyRef.current = 0;
    guRef.current = new GameUtils(null, pz.side_to_move === 'w', false, true, true);
    busyRef.current = false;
    setPosition(pz.fen);
    setLastArrow(pz.last_move
      ? [[pz.last_move.split(',')[0], pz.last_move.split(',')[1], 'rgba(0,0,0,0.4)']] : []);
    const done = (doneMap || solved)[pz.id];
    setStatus(done ? 'solved' : 'playing');
    setMessage(done ? (t('puzzle_solved') || 'Solved!') : '');
    setMsgKind(done ? 'solved' : '');
    clearAlert();
  };

  const markSolved = (pz) => {
    setSolved((prev) => {
      const next = { ...prev, [pz.id]: true };
      try {
        localStorage.setItem('sumo_puzzle_solved_' + (pz.puzzle_date || 'today'), JSON.stringify(next));
      } catch (e) { /* ignore */ }
      return next;
    });
  };

  const applyMove = (move, moverColor) => {
    const working = new Chess(gameRef.current.fen());
    guRef.current.isWhite = moverColor === 'w';
    guRef.current.myTurn = true;
    const res = guRef.current.makeAMove(move, moverColor, false, working, lastMoveRef.current, () => {});
    if (!res || res.error) return null;
    gameRef.current = new Chess(res.currentFen);
    lastMoveRef.current = res.lastMoveAsString;
    return res;
  };

  const buildMove = (source, target, piece) => {
    const move = { from: source, to: target };
    if (piece && piece[1] === 'P' && (target[1] === '8' || target[1] === '1')) move.promotion = 'q';
    return move;
  };

  const onDrop = (source, target, piece) => {
    const pz = puzzleRef.current;
    if (status !== 'playing' || busyRef.current || !pz) return false;

    const humanColor = pz.side_to_move;
    const oppColor = humanColor === 'w' ? 'b' : 'w';
    const prevFen = gameRef.current.fen();
    const prevLast = lastMoveRef.current;
    const myPly = plyRef.current;

    const res = applyMove(buildMove(source, target, piece), humanColor);
    if (!res) { flashAlert(t('puzzle_illegal') || 'That is not a legal move.'); return false; }

    setPosition(res.currentFen);
    setLastArrow([[source, target, 'rgba(30,130,255,0.5)']]);
    setMessage("");
    setMsgKind("");
    clearAlert();
    busyRef.current = true;

    PuzzleService.step(pz.id, myPly, `${source},${target}`)
      .then((resp) => {
        const data = resp.data || {};
        if (!data.correct) {
          gameRef.current = new Chess(prevFen);
          lastMoveRef.current = prevLast;
          setPosition(prevFen);
          setLastArrow(prevLast ? [[prevLast.split(',')[0], prevLast.split(',')[1], 'rgba(0,0,0,0.4)']] : []);
          recordFail(pz);
          flashAlert(randomInsult());
          setMessage("");
          setMsgKind("");
          busyRef.current = false;
          return;
        }
        if (data.solved) {
          plyRef.current = myPly + 1;
          setStatus('solved');
          if (!solved[pz.id]) { markSolved(pz); recordSolve(); }
          setMessage(t('puzzle_solved') || 'Solved! Well done.');
          setMsgKind('solved');
          busyRef.current = false;
          return;
        }
        const [rf, rt] = data.reply.split(',');
        const oppRes = applyMove({ from: rf, to: rt }, oppColor);
        if (oppRes) { setPosition(oppRes.currentFen); setLastArrow([[rf, rt, 'rgba(200,60,60,0.55)']]); }
        plyRef.current = myPly + 2;
        setMessage(t('puzzle_keep_going') || 'Correct! Keep going.');
        setMsgKind('keep');
        busyRef.current = false;
      })
      .catch(() => {
        gameRef.current = new Chess(prevFen);
        lastMoveRef.current = prevLast;
        setPosition(prevFen);
        setMessage(t('puzzle_error') || 'Something went wrong. Try again.');
        setMsgKind('');
        busyRef.current = false;
      });
    return true;
  };

  const smallWidth = windowDimensions.width <= 480;
  const prct = smallWidth ? 0.92 : 0.8;
  const minSize = Math.min(windowDimensions.width, windowDimensions.height - HEADER_HEIGHT - 160) * prct;

  // --- calendar helpers ---------------------------------------------------
  const todayStr = todayISO();
  const isToday = !selectedDate || selectedDate === todayStr;

  const shiftMonth = (delta) => {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  };
  const onPickDate = (dateStr) => {
    if (!availDates.has(dateStr)) return;
    loadPuzzles(dateStr === todayStr ? null : dateStr);
  };
  const goToday = () => {
    const d = new Date();
    setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    loadPuzzles(null);
  };

  const selectedLabel = selectedDate
    ? dateFromISO(selectedDate).toLocaleDateString(lang, { weekday: 'short', month: 'short', day: 'numeric' })
    : (t('puzzle_today') || 'Today');

  const renderCalendar = () => {
    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth();
    const startDow = new Date(y, m, 1).getDay();          // 0=Sun
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    // narrow weekday labels (2023-01-01 was a Sunday)
    const wd = Array.from({ length: 7 }, (_, i) =>
      new Date(2023, 0, 1 + i).toLocaleDateString(lang, { weekday: 'narrow' }));
    const monthLabel = viewMonth.toLocaleDateString(lang, { month: 'long', year: 'numeric' });
    const atCurrentMonth = y > new Date().getFullYear()
      || (y === new Date().getFullYear() && m >= new Date().getMonth());

    const navBtn = (label, onPress, disabled) => (
      <TouchableOpacity onPress={disabled ? undefined : onPress} disabled={disabled}
        style={{ paddingHorizontal: 12, paddingVertical: 4, opacity: disabled ? 0.3 : 1 }}>
        <Text style={{ color: '#f5c542', fontSize: 20, fontWeight: 'bold' }}>{label}</Text>
      </TouchableOpacity>
    );

    return (
      <View style={{
        backgroundColor: '#2a0f0c', borderRadius: 10, padding: 12, marginBottom: 14,
        borderWidth: 1, borderColor: 'rgba(209,160,56,0.4)', width: 300,
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          {navBtn('‹', () => shiftMonth(-1), false)}
          <Text style={{ color: '#fff', fontSize: 15, fontWeight: 'bold', textTransform: 'capitalize' }}>{monthLabel}</Text>
          {navBtn('›', () => shiftMonth(1), atCurrentMonth)}
        </View>

        <View style={{ flexDirection: 'row', marginBottom: 4 }}>
          {wd.map((w, i) => (
            <Text key={i} style={{
              flex: 1, textAlign: 'center', color: 'rgba(255,255,255,0.5)',
              fontSize: 12, fontWeight: 'bold',
            }}>{w}</Text>
          ))}
        </View>

        {weeks.map((week, wi) => (
          <View key={wi} style={{ flexDirection: 'row' }}>
            {week.map((d, di) => {
              if (d === null) return <View key={di} style={{ flex: 1, height: 34 }} />;
              const ds = isoOf(y, m, d);
              const avail = availDates.has(ds);
              const sel = ds === selectedDate;
              const isTd = ds === todayStr;
              return (
                <TouchableOpacity key={di} onPress={() => onPickDate(ds)} disabled={!avail}
                  style={{
                    flex: 1, height: 34, margin: 1, borderRadius: 6,
                    alignItems: 'center', justifyContent: 'center',
                    backgroundColor: sel ? '#A1291F' : avail ? 'rgba(209,160,56,0.18)' : 'transparent',
                    borderWidth: (isTd && !sel) ? 1 : 0, borderColor: '#D1A038',
                  }}>
                  <Text style={{
                    color: avail ? '#fff' : 'rgba(255,255,255,0.25)',
                    fontWeight: (sel || isTd) ? 'bold' : 'normal', fontSize: 14,
                  }}>{d}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        ))}

        <TouchableOpacity onPress={goToday} style={{ marginTop: 8, alignItems: 'center' }}>
          <Text style={{ color: '#f5c542', fontSize: 14 }}>{t('puzzle_today') || 'Today'}</Text>
        </TouchableOpacity>
      </View>
    );
  };
  // ------------------------------------------------------------------------

  const pz = status === 'playing' || status === 'solved' ? puzzleRef.current : null;
  const toMove = pz && (pz.side_to_move === 'w' ? (t('white_to_move') || 'White to move')
    : (t('black_to_move') || 'Black to move'));
  const goal = pz && (t('puzzle_goal') || 'Mate in %d').replace('%d', pz.mate_in);

  return (
    <View style={{ alignItems: 'center', marginTop: 18 }}>
      <Text style={{ color: '#fff', fontSize: 22, fontWeight: 'bold', marginBottom: 10 }}>
        {isToday ? (t('puzzle_of_the_day') || 'Puzzle of the Day')
          : dateFromISO(selectedDate).toLocaleDateString(lang, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
      </Text>

      {/* date picker toggle */}
      <TouchableOpacity onPress={() => setShowCal((s) => !s)}
        style={{
          flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12,
          paddingVertical: 6, paddingHorizontal: 14, borderRadius: 8,
          backgroundColor: 'rgba(255,255,255,0.10)',
          borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
        }}>
        <Text style={{ color: '#fff', fontSize: 15 }}>📅 {selectedLabel}</Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{showCal ? '▲' : '▼'}</Text>
      </TouchableOpacity>

      {showCal && renderCalendar()}

      {/* rank (accuracy-weighted) */}
      <Text style={{ color: '#D1A038', fontSize: 16, fontWeight: 'bold', marginBottom: 6 }}>
        {(t('puzzle_level') || 'Level')}: {t('level_' + (levelIndex(effectiveScore(stats.totalSolved, stats.failed)) + 1))}
        {stats.totalSolved > 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, fontWeight: 'normal' }}>
            {'  '}· {accuracyPct(stats.totalSolved, stats.failed)}% {t('puzzle_accuracy') || 'accuracy'}
          </Text>
        )}
      </Text>

      {/* lifetime stats */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 18, marginBottom: 12 }}>
        <Text style={{ color: '#f5c542', fontSize: 15, fontWeight: 'bold' }}>
          🔥 {stats.streak} {t('puzzle_stat_streak') || 'day streak'}
        </Text>
        <Text style={{ color: '#fff', fontSize: 15 }}>
          ✓ {stats.totalSolved} {t('puzzle_stat_solved') || 'solved'}
        </Text>
        <Text style={{ color: '#ff6b6b', fontSize: 15 }}>
          💀 {stats.failed || 0} {t('puzzle_stat_failed') || 'failed'}
        </Text>
        {stats.bestStreak > 0 && (
          <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>
            🏆 {stats.bestStreak} {t('puzzle_stat_best') || 'best'}
          </Text>
        )}
      </View>

      {status === 'loading' && (
        <Text style={{ color: '#fff', fontSize: 18, marginTop: 30 }}>{t('loading') || 'Loading…'}</Text>
      )}

      {(status === 'none' || status === 'error') && (
        <Text style={{ color: '#fff', fontSize: 18, textAlign: 'center', maxWidth: 400, marginTop: 30 }}>
          {status === 'none' ? (t('puzzle_none') || 'No puzzle for that day — pick another.')
            : (t('puzzle_error') || 'Could not load the puzzle.')}
        </Text>
      )}

      {pz && (
        <>
          {/* difficulty switcher */}
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
            {puzzles.map((p, i) => {
              const isSel = i === selected;
              const isDone = !!solved[p.id];
              return (
                <TouchableOpacity key={p.id} onPress={() => selectPuzzle(puzzles, i)}
                  style={{
                    paddingVertical: 7, paddingHorizontal: 16, borderRadius: 8,
                    backgroundColor: isSel ? '#A1291F' : 'rgba(255,255,255,0.10)',
                    borderWidth: 1, borderColor: isSel ? '#D1A038' : 'rgba(255,255,255,0.15)',
                  }}>
                  <Text style={{ color: '#fff', fontWeight: isSel ? 'bold' : 'normal', fontSize: 15 }}>
                    {(DIFF_LABELS[p.difficulty_name] || p.difficulty_name)}{isDone ? '  ✓' : ''}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={{ color: '#f5c542', fontSize: 15, marginBottom: 12 }}>{toMove} · {goal}</Text>

          <View style={{ width: minSize + 'px', position: 'relative' }}>
            <Chessboard
              id="PuzzleBoard"
              position={position}
              onPieceDrop={onDrop}
              arePiecesDraggable={status === 'playing'}
              customArrows={lastArrow}
              boardOrientation={pz.side_to_move === 'w' ? 'white' : 'black'}
              customBoardStyle={{ boxShadow: '0 5px 25px rgba(0,0,0,0.5)', borderRadius: '6px' }}
              customLightSquareStyle={{ backgroundColor: '#D1A038' }}
              customDarkSquareStyle={{ backgroundColor: '#A1291F' }}
            />

            {/* wrong-move alert, flashed big over the board */}
            {alertMsg !== '' && (
              <View pointerEvents="none" style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                alignItems: 'center', justifyContent: 'center',
              }}>
                <View style={{
                  backgroundColor: 'rgba(161,41,31,0.96)',
                  paddingVertical: 18, paddingHorizontal: 24, borderRadius: 14,
                  borderWidth: 3, borderColor: '#D1A038', maxWidth: '88%',
                  shadowColor: '#000', shadowOpacity: 0.6, shadowRadius: 20,
                  shadowOffset: { width: 0, height: 10 }, elevation: 12,
                }}>
                  <Text style={{ color: '#fff', fontSize: 12, fontWeight: 'bold',
                    letterSpacing: 2, textAlign: 'center', marginBottom: 6, opacity: 0.85 }}>
                    ✗ {(t('puzzle_wrong_label') || 'WRONG MOVE')}
                  </Text>
                  <Text style={{ color: '#fff', fontSize: 22, fontWeight: 'bold',
                    fontStyle: 'italic', textAlign: 'center', lineHeight: 28 }}>
                    {alertMsg}
                  </Text>
                </View>
              </View>
            )}
          </View>

          {message !== '' && (
            <Text style={{
              color: msgKind === 'solved' ? '#5ad469' : msgKind === 'wrong' ? '#ff6b6b' : '#fff',
              fontSize: 17, marginTop: 16, fontStyle: msgKind === 'wrong' ? 'italic' : 'normal',
              textAlign: 'center', maxWidth: Math.max(minSize, 400),
            }}>{message}</Text>
          )}
        </>
      )}
    </View>
  );
}
